import { z } from 'zod';
import { localDateSchema, timezoneSchema } from './common.js';
/**
 * Adaptive TDEE — maintenance calories measured rather than predicted.
 *
 * A Harris-Benedict or Mifflin equation guesses your expenditure from height,
 * weight, age and a lifestyle multiplier picked off a dropdown. It is a
 * population average applied to one person, and the activity multiplier alone
 * spans 600 kcal.
 *
 * This measures instead. Over a long enough window, energy balance is an
 * identity: whatever you ate and did not store or release as body mass, you
 * spent. So
 *
 *     TDEE = mean intake − (change in trend weight × kcal per kg) / days
 *
 * It needs no body composition, no activity guess, and it silently accounts for
 * everything a formula cannot — NEAT, thermic effect, an unusually cold flat.
 * What it does need is honest data, which is why the failure states below are
 * as much of this contract as the number is.
 */
/**
 * Energy per kilogram of body mass gained or lost, in kcal.
 *
 * The Wishnofsky figure (3500 kcal/lb). It describes adipose tissue, and real
 * weight change is a mix of fat, lean mass and glycogen-bound water, so it is
 * an approximation — but the alternative is modelling body composition from a
 * bathroom scale, which would be a worse approximation wearing a lab coat.
 */
export const KCAL_PER_KG = 7700;
export const tdeeStatusSchema = z.enum([
    'ok',
    /** The preference is off. Nothing was computed. */
    'disabled',
    /** The window has not been open long enough to average anything out. */
    'insufficient_window',
    /** Too many days in the window have no entries for the mean to mean anything. */
    'insufficient_logging',
    /** Not enough weigh-ins spanning the window to know which way the trend went. */
    'insufficient_weight_data',
    /** The arithmetic produced a number no human body would report. */
    'implausible',
]);
export const tdeeQuerySchema = z.object({
    /**
     * Days to look back. Longer is more accurate — a week of glycogen swings can
     * move the apparent trend by a kilo — but takes longer to react to a real
     * change in habits.
     */
    days: z.coerce.number().int().min(14).max(120).default(28),
    timezone: timezoneSchema.optional(),
});
export const tdeeEstimateSchema = z.object({
    status: tdeeStatusSchema,
    /** Maintenance calories per day. Non-null only when `status` is `ok`. */
    tdeeKcal: z.number().nullable(),
    from: localDateSchema.nullable(),
    to: localDateSchema.nullable(),
    /** Calendar days the window spans. */
    windowDays: z.number().int(),
    /** How many of those days have at least one diary entry. */
    loggedDays: z.number().int(),
    /**
     * Mean intake across *logged* days.
     *
     * Deliberately not "across all days": an unlogged day is unknown, not zero.
     * It does mean the estimate assumes unlogged days resembled logged ones,
     * which is exactly why coverage has to be high before a number is returned.
     */
    averageIntakeKcal: z.number().nullable(),
    /** Movement of the smoothed trend line across the window, in kg. */
    trendChangeKg: z.number().nullable(),
    ratePerWeekKg: z.number().nullable(),
    /**
     * What to eat to hit the goal weight's direction, if a weight goal is set:
     * the estimate minus a deficit, or plus a surplus, capped at a rate the body
     * can actually sustain.
     */
    suggestedGoalKcal: z.number().nullable(),
});
//# sourceMappingURL=tdee.js.map